import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  FilmStrip, Coins, Plus, Play, CircleNotch, CheckCircle, XCircle, Clock,
  SignOut, Lightning, VideoCamera, X, Gift
} from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const vcxApi = axios.create({ baseURL: `${API_URL}/api/vcx` });
vcxApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem('vcx_token');
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const errMsg = (e) => {
  const d = e.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || '').join(' ');
  return 'Something went wrong — please try again.';
};

const STATUS_META = {
  queued: { label: 'Queued', cls: 'bg-blue-500/15 text-blue-300 border-blue-500/40', Icon: Clock },
  recording: { label: 'Recording', cls: 'bg-amber-500/15 text-amber-300 border-amber-500/40', Icon: VideoCamera },
  published: { label: 'Published', cls: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40', Icon: CheckCircle },
  failed: { label: 'Failed — credits refunded', cls: 'bg-red-500/15 text-red-300 border-red-500/40', Icon: XCircle },
};

function AuthGate({ onAuthed }) {
  const [mode, setMode] = useState('register');
  const [form, setForm] = useState({ email: '', password: '', name: '', website: '' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const go = async () => {
    setBusy(true); setError('');
    try {
      const { data } = await vcxApi.post(`/auth/${mode}`, form);
      localStorage.setItem('vcx_token', data.token);
      onAuthed(data.user);
    } catch (e) { setError(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="max-w-md mx-auto pt-14 pb-20 px-6" data-testid="vcx-auth-gate">
      <div className="text-center mb-8">
        <h1 className="text-3xl font-black tracking-tight mb-2">{mode === 'register' ? 'Create your account' : 'Welcome back'}</h1>
        <p className="text-sm text-white/60">{mode === 'register' ? 'Your first video is free — up to 2 minutes, on us.' : 'Sign in to your VideoCrunch dashboard.'}</p>
      </div>
      <div className="bg-white/5 border border-white/15 rounded-xl p-6 space-y-4">
        {mode === 'register' && (
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })}
            className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" data-testid="vcx-auth-website-hp" />
        )}
        {mode === 'register' && (
          <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name"
            className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-auth-name" />
        )}
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com"
          className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-auth-email" />
        <input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={mode === 'register' ? 'Password (8+ characters)' : 'Password'}
          onKeyDown={(e) => e.key === 'Enter' && go()}
          className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-auth-password" />
        {error && <p className="text-xs text-red-300" data-testid="vcx-auth-error">{error}</p>}
        <button onClick={go} disabled={busy}
          className="w-full inline-flex items-center justify-center gap-2 bg-[#F39C12] hover:bg-[#D68910] disabled:opacity-40 text-slate-900 font-black text-sm px-6 py-3 rounded-lg transition-colors"
          data-testid="vcx-auth-submit">
          {busy && <CircleNotch size={15} className="animate-spin" />}
          {mode === 'register' ? 'Create account — first video free' : 'Sign in'}
        </button>
        <button onClick={() => { setMode(mode === 'register' ? 'login' : 'register'); setError(''); }}
          className="w-full text-xs text-white/50 hover:text-white" data-testid="vcx-auth-switch">
          {mode === 'register' ? 'Already have an account? Sign in' : "New here? Create an account — first video's free"}
        </button>
      </div>
    </div>
  );
}

function AddCreditsModal({ config, onClose, onBuy, busyPack, user, onCancelPlan, cancelBusy }) {
  const [confirmCancel, setConfirmCancel] = useState(false);
  const subscriptionActive = user?.subscription_active;
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" data-testid="vcx-credits-modal">
      <div className="bg-[#101636] border border-white/15 rounded-2xl max-w-2xl w-full p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-white/50 hover:text-white" data-testid="vcx-credits-close"><X size={20} /></button>
        <h2 className="text-xl font-black mb-1 flex items-center gap-2"><Coins size={20} weight="bold" className="text-[#F39C12]" />Add credits</h2>
        <p className="text-xs text-white/50 mb-5">10 credits = 1 minute of finished video, billed to the second. Purchased credits never expire.</p>
        {!subscriptionActive && config?.subscription && (
          <button onClick={() => onBuy('subscription')} disabled={!!busyPack}
            className="w-full text-left bg-[#F39C12]/10 border-2 border-[#F39C12]/60 rounded-xl p-4 mb-4 hover:bg-[#F39C12]/20 transition-colors"
            data-testid="vcx-buy-subscription">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-black text-[#F39C12]">${config.subscription.amount}/month — {config.subscription.monthly_credits} credits every month</div>
                <div className="text-xs text-white/50 mt-1">The best per-credit price. Credits refresh monthly. Cancel anytime.</div>
              </div>
              {busyPack === 'subscription' ? <CircleNotch size={18} className="animate-spin text-[#F39C12]" /> : <Lightning size={18} weight="bold" className="text-[#F39C12]" />}
            </div>
          </button>
        )}
        <div className="grid sm:grid-cols-3 gap-3">
          {(config?.packs || []).map((p) => (
            <button key={p.pack_id} onClick={() => onBuy(p.pack_id)} disabled={!!busyPack}
              className="relative bg-white/5 border border-white/15 hover:border-[#F39C12]/60 rounded-xl p-4 text-center transition-colors"
              data-testid={`vcx-buy-${p.pack_id}`}>
              {p.badge && <span className="absolute -top-2 left-1/2 -translate-x-1/2 bg-[#F39C12] text-slate-900 text-[9px] font-black uppercase px-2 py-0.5 rounded-full whitespace-nowrap">{p.badge}</span>}
              <div className="text-2xl font-black">{p.credits}</div>
              <div className="text-[10px] uppercase tracking-wider text-white/40 font-bold mb-2">credits</div>
              <div className="text-sm font-black text-[#F39C12]">{busyPack === p.pack_id ? <CircleNotch size={16} className="animate-spin mx-auto" /> : `$${p.amount}`}</div>
            </button>
          ))}
        </div>
        {subscriptionActive && (
          <div className="mt-5 border-t border-white/10 pt-4" data-testid="vcx-manage-plan">
            {user.subscription_cancel_at_period_end ? (
              <p className="text-xs text-white/50" data-testid="vcx-plan-ending">
                Your plan is cancelled and stays active until {user.subscription_period_end ? new Date(user.subscription_period_end).toLocaleDateString() : 'the end of your billing period'} — remaining monthly credits are yours to use until then. Purchased credits never expire.
              </p>
            ) : confirmCancel ? (
              <div className="flex flex-wrap items-center gap-3" data-testid="vcx-cancel-confirm-row">
                <p className="text-xs text-white/60 flex-1 min-w-[220px]">Cancel your ${config?.subscription?.amount ?? 49}/mo plan? You keep your monthly credits until the end of the billing period, and purchased credits never expire.</p>
                <button onClick={onCancelPlan} disabled={cancelBusy}
                  className="text-xs font-black text-red-300 border border-red-400/40 hover:bg-red-500/10 disabled:opacity-40 rounded-lg px-3 py-2 transition-colors"
                  data-testid="vcx-cancel-confirm-btn">
                  {cancelBusy ? 'Cancelling…' : 'Yes, cancel my plan'}
                </button>
                <button onClick={() => setConfirmCancel(false)} className="text-xs text-white/50 hover:text-white" data-testid="vcx-cancel-keep-btn">Keep plan</button>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-white/50">Your ${config?.subscription?.amount ?? 49}/mo plan is active — {config?.subscription?.monthly_credits ?? 50} credits refresh monthly.</p>
                <button onClick={() => setConfirmCancel(true)}
                  className="text-xs text-white/40 hover:text-red-300 underline underline-offset-2 shrink-0 transition-colors"
                  data-testid="vcx-cancel-plan-btn">Cancel plan</button>
              </div>
            )}
          </div>
        )}
        <p className="text-[10px] text-white/30 mt-4 text-center">Payments are processed securely by Stripe.</p>
      </div>
    </div>
  );
}

export default function VideoCrunchAppPage() {
  const [user, setUser] = useState(undefined); // undefined=checking, null=anon
  const [config, setConfig] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showCredits, setShowCredits] = useState(false);
  const [busyPack, setBusyPack] = useState('');
  const [form, setForm] = useState({ title: '', app_url: '', demo_login: '', demo_password: '', steps_text: '', target_minutes: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  const [notice, setNotice] = useState('');
  const [searchParams, setSearchParams] = useSearchParams();

  const loadAll = useCallback(async () => {
    try {
      const [me, jb] = await Promise.all([vcxApi.get('/me'), vcxApi.get('/jobs')]);
      setUser(me.data.user);
      setJobs(jb.data.jobs);
    } catch {
      localStorage.removeItem('vcx_token');
      setUser(null);
    }
  }, []);

  useEffect(() => {
    axios.get(`${API_URL}/api/vcx/config`).then((r) => setConfig(r.data)).catch(() => {});
    if (localStorage.getItem('vcx_token')) loadAll(); else setUser(null);
  }, [loadAll]);

  // Payment return: poll status until credited
  useEffect(() => {
    const sid = searchParams.get('session_id');
    if (!sid || !localStorage.getItem('vcx_token')) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const { data } = await vcxApi.get(`/billing/status/${sid}`);
        if (data.credited) {
          setUser(data.user);
          setNotice(data.kind === 'vcx_subscription' ? '🎉 Subscription active — 50 credits deposited!' : '🎉 Credits added to your balance!');
          setSearchParams({}, { replace: true });
          return;
        }
      } catch { /* keep polling */ }
      if (tries < 12) setTimeout(poll, 2500);
      else { setNotice('Payment is processing — your credits will appear shortly.'); setSearchParams({}, { replace: true }); }
    };
    poll();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const buy = async (what) => {
    setBusyPack(what);
    try {
      const origin_url = window.location.origin;
      // Rewardful affiliate attribution — forwarded to Stripe as client_reference_id
      const referral_id = (window.Rewardful && window.Rewardful.referral) || null;
      const { data } = what === 'subscription'
        ? await vcxApi.post('/billing/subscribe', { origin_url, referral_id })
        : await vcxApi.post('/billing/pack', { pack_id: what, origin_url, referral_id });
      window.location.href = data.url;
    } catch (e) {
      setNotice(errMsg(e));
      setBusyPack('');
    }
  };

  const submitJob = async () => {
    setFormError('');
    setSubmitting(true);
    try {
      const { data } = await vcxApi.post('/jobs', form);
      setJobs((prev) => [data.job, ...prev]);
      setUser(data.user);
      setForm({ title: '', app_url: '', demo_login: '', demo_password: '', steps_text: '', target_minutes: 1 });
      setNotice(data.job.is_free_trial ? '🎬 Free video queued! We\'ll start recording shortly.' : `🎬 Video queued — ${data.job.estimated_credits} credits reserved (unused seconds are refunded).`);
    } catch (e) {
      const d = e.response?.data?.detail || '';
      if (typeof d === 'string' && d.startsWith('out_of_credits')) {
        setShowCredits(true);
        setFormError('You\'re out of credits — add credits to queue this video.');
      } else setFormError(errMsg(e));
    }
    setSubmitting(false);
  };

  const logout = () => { localStorage.removeItem('vcx_token'); setUser(null); setJobs([]); };

  const [cancelBusy, setCancelBusy] = useState(false);
  const cancelPlan = async () => {
    setCancelBusy(true);
    try {
      const { data } = await vcxApi.post('/billing/cancel');
      setUser(data.user);
      setNotice(data.period_end
        ? `Plan cancelled — it stays active (with your credits) until ${new Date(data.period_end).toLocaleDateString()}.`
        : 'Plan cancelled — it stays active until the end of your billing period.');
      setShowCredits(false);
    } catch (e) {
      setNotice(errMsg(e));
    }
    setCancelBusy(false);
  };

  const freeEligible = user && !user.free_video_used;

  return (
    <div className="min-h-screen bg-[#0a0e27] text-white" data-testid="vcx-app-page">
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/videocrunch" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#F39C12] rounded flex items-center justify-center"><FilmStrip size={18} weight="bold" className="text-slate-900" /></div>
            <span className="text-lg font-black tracking-tight">Video<span className="text-[#F39C12]">Crunch</span></span>
          </Link>
          {user && (
            <div className="flex items-center gap-3">
              <button onClick={() => setShowCredits(true)} className="flex items-center gap-2 bg-white/5 border border-white/15 hover:border-[#F39C12]/60 rounded-full px-4 py-1.5 transition-colors" data-testid="vcx-balance-chip">
                <Coins size={15} weight="bold" className="text-[#F39C12]" />
                <span className="text-sm font-black">{user.total_credits}</span>
                <span className="text-[10px] text-white/40 font-bold uppercase">credits</span>
                <Plus size={13} weight="bold" className="text-[#F39C12]" />
              </button>
              <button onClick={logout} className="text-white/40 hover:text-white" title="Sign out" data-testid="vcx-logout-btn"><SignOut size={18} /></button>
            </div>
          )}
        </div>
      </header>

      {notice && (
        <div className="max-w-4xl mx-auto px-6 pt-4">
          <div className="bg-[#F39C12]/10 border border-[#F39C12]/40 text-[#F39C12] text-sm font-bold rounded-lg px-4 py-3 flex items-center justify-between" data-testid="vcx-notice">
            <span>{notice}</span>
            <button onClick={() => setNotice('')} className="text-[#F39C12]/60 hover:text-[#F39C12]"><X size={15} /></button>
          </div>
        </div>
      )}

      {user === undefined ? (
        <div className="flex justify-center pt-24"><CircleNotch size={28} className="animate-spin text-[#F39C12]" /></div>
      ) : user === null ? (
        <AuthGate onAuthed={(u) => { setUser(u); loadAll(); }} />
      ) : (
        <main className="max-w-4xl mx-auto px-6 py-8">
          {freeEligible && (
            <div className="flex items-start gap-3 bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4 mb-6" data-testid="vcx-free-banner">
              <Gift size={20} weight="bold" className="text-emerald-400 shrink-0 mt-0.5" />
              <p className="text-sm text-emerald-200"><strong>Your first video is free</strong> — up to 2 minutes, zero credits. Queue it below.</p>
            </div>
          )}

          {/* New video form */}
          <section className="bg-white/5 border border-white/15 rounded-xl p-6 mb-8" data-testid="vcx-job-form">
            <h2 className="text-base font-bold mb-4 flex items-center gap-2"><VideoCamera size={18} weight="bold" className="text-[#F39C12]" />Request a video</h2>
            <div className="grid sm:grid-cols-2 gap-4 mb-4">
              <input type="text" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Video title — e.g. 'How to create an invoice'"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-job-title" />
              <input type="text" value={form.app_url} onChange={(e) => setForm({ ...form, app_url: e.target.value })} placeholder="Your app URL (staging works great)"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-job-url" />
              <input type="text" value={form.demo_login} onChange={(e) => setForm({ ...form, demo_login: e.target.value })} placeholder="Demo login (email/username) — optional"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-job-login" />
              <input type="text" value={form.demo_password} onChange={(e) => setForm({ ...form, demo_password: e.target.value })} placeholder="Demo password — optional"
                className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-job-password" />
            </div>
            <textarea value={form.steps_text} onChange={(e) => setForm({ ...form, steps_text: e.target.value })} rows={4} maxLength={3000}
              placeholder={'Describe the walkthrough in plain English, e.g.:\n"Log in, open the Invoices tab, create a new invoice for a customer, add two line items, show the total updating, then send it."'}
              className="w-full bg-black/30 border border-white/15 rounded-lg px-3 py-2.5 text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#F39C12]/60 resize-y mb-4" data-testid="vcx-job-steps" />
            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-white/60 font-bold">
                Target length:
                <select value={form.target_minutes} onChange={(e) => setForm({ ...form, target_minutes: parseInt(e.target.value) })}
                  className="bg-black/30 border border-white/15 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-[#F39C12]/60" data-testid="vcx-job-minutes">
                  {[1, 2, 3, 4, 5, 6, 8, 10].map((m) => <option key={m} value={m}>{m} min{m > 1 ? 's' : ''}</option>)}
                </select>
              </label>
              <span className="text-xs text-white/40" data-testid="vcx-job-cost">
                {freeEligible && form.target_minutes <= 2 ? 'Cost: FREE (first video)' : `Reserves ${form.target_minutes * 10} credits — unused seconds refunded`}
              </span>
              <button onClick={submitJob} disabled={submitting || form.title.length < 2 || form.app_url.length < 8 || form.steps_text.length < 10}
                className="ml-auto inline-flex items-center gap-2 bg-[#F39C12] hover:bg-[#D68910] disabled:opacity-40 text-slate-900 font-black text-sm px-6 py-2.5 rounded-lg transition-colors"
                data-testid="vcx-job-submit">
                {submitting ? <CircleNotch size={15} className="animate-spin" /> : <Play size={15} weight="fill" />}Queue my video
              </button>
            </div>
            {formError && <p className="mt-3 text-xs text-red-300" data-testid="vcx-job-error">{formError}</p>}
          </section>

          {/* Jobs */}
          <section data-testid="vcx-jobs-list">
            <h2 className="text-base font-bold mb-4">My videos</h2>
            {jobs.length === 0 ? (
              <p className="text-sm text-white/40 border border-dashed border-white/15 rounded-xl p-6 text-center">No videos yet — request your first one above.</p>
            ) : (
              <div className="space-y-4">
                {jobs.map((j) => {
                  const meta = STATUS_META[j.status] || STATUS_META.queued;
                  return (
                    <div key={j.job_id} className="bg-white/5 border border-white/15 rounded-xl p-5" data-testid={`vcx-job-${j.job_id}`}>
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h3 className="text-sm font-bold">{j.title}</h3>
                        <span className={`inline-flex items-center gap-1.5 text-[11px] font-black px-2.5 py-1 rounded-full border ${meta.cls}`}>
                          <meta.Icon size={12} weight="bold" />{meta.label}
                        </span>
                        {j.is_free_trial && <span className="text-[10px] font-black uppercase text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-2 py-0.5 rounded-full">Free video</span>}
                      </div>
                      <p className="text-xs text-white/40 mb-1">{j.app_url} · target {j.target_minutes} min · {new Date(j.created_at).toLocaleDateString()}</p>
                      {j.status === 'published' && (
                        <div className="mt-3">
                          <video controls preload="metadata" className="w-full max-w-xl aspect-video bg-black rounded-lg" data-testid={`vcx-job-video-${j.job_id}`}>
                            <source src={j.video_url} type="video/mp4" />
                          </video>
                          <p className="text-xs text-white/40 mt-2">
                            {Math.floor(j.actual_seconds / 60)}:{String(j.actual_seconds % 60).padStart(2, '0')} · {j.is_free_trial ? 'free' : `${j.actual_credits} credits`}
                          </p>
                        </div>
                      )}
                      {j.status === 'failed' && j.fail_reason && <p className="text-xs text-red-300 mt-2">{j.fail_reason}</p>}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        </main>
      )}

      {showCredits && config && (
        <AddCreditsModal config={config} onClose={() => setShowCredits(false)} onBuy={buy}
          busyPack={busyPack} user={user} onCancelPlan={cancelPlan} cancelBusy={cancelBusy} />
      )}
    </div>
  );
}
